import { useSyncExternalStore } from "react";

export type InactivityLevel = "active" | "idle1" | "idle2";

const IDLE1_MS = 60_000;
const IDLE2_MS = 300_000;
const MOUSEMOVE_THROTTLE_MS = 1000;

const listeners = new Set<() => void>();
let current: InactivityLevel = "active";
let lastActivity = typeof performance !== "undefined" ? performance.now() : Date.now();
let lastMousemove = 0;
let attached = false;
let tickHandle: ReturnType<typeof setInterval> | null = null;

function read(): InactivityLevel {
  return current;
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function notify() {
  for (const listener of listeners) listener();
}

function set(next: InactivityLevel) {
  if (next !== current) {
    current = next;
    notify();
  }
}

function tick() {
  const elapsed = now() - lastActivity;
  if (elapsed >= IDLE2_MS) set("idle2");
  else if (elapsed >= IDLE1_MS) set("idle1");
  else set("active");
}

function bump() {
  lastActivity = now();
  if (current !== "active") set("active");
}

function onMouseMove() {
  const t = now();
  if (t - lastMousemove < MOUSEMOVE_THROTTLE_MS) return;
  lastMousemove = t;
  bump();
}

function ensureAttached() {
  if (attached || typeof window === "undefined") return;
  attached = true;
  window.addEventListener("mousemove", onMouseMove, { passive: true });
  window.addEventListener("keydown", bump, { passive: true });
  window.addEventListener("pointerdown", bump, { passive: true });
  window.addEventListener("touchstart", bump, { passive: true });
  tickHandle = setInterval(tick, 1000);
}

function subscribe(listener: () => void) {
  ensureAttached();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useInactivityLevel(): InactivityLevel {
  return useSyncExternalStore(subscribe, read, read);
}

export function getInactivityLevel(): InactivityLevel {
  return current;
}

// Exported for tests only.
export const __test__ = {
  reset() {
    current = "active";
    lastActivity = now();
    lastMousemove = 0;
    if (tickHandle) {
      clearInterval(tickHandle);
      tickHandle = null;
    }
    attached = false;
    listeners.clear();
  },
};
